import React, { useState } from "react";
import { useSelector } from "react-redux";
import PRICING from "../utils/PRICING.json";

// POST Cooldown constant (matching backend)
const POST_COOLDOWN = 10 * 1000; // 10 seconds cooldown between POSTs

// Backend Error Messages with Translations
const ERROR_MESSAGES = {
  // IP Blocked
  "Your IP has been blocked due to suspicious activity. Please try again later.": {
    en: "Your IP has been blocked due to suspicious activity. Please try again later.",
    fr: "Votre IP a été bloquée en raison d'une activité suspecte. Veuillez réessayer plus tard.",
    ar: "تم حظر عنوان IP الخاص بك بسبب نشاط مريب. يرجى محاولة مجددًا لاحقًا."
  },
  // Invalid request format
  "Invalid request format": {
    en: "Invalid request format. Please check your input.",
    fr: "Format de demande invalide. Veuillez vérifier votre saisie.",
    ar: "تنسيق الطلب غير صحيح. يرجى التحقق من إدخالك."
  },
  // Email validation errors
  "Email is required": {
    en: "Email is required",
    fr: "L'email est obligatoire",
    ar: "البريد الإلكتروني مطلوب"
  },
  "Please provide a valid email address": {
    en: "Please provide a valid email address",
    fr: "Veuillez fournir une adresse e-mail valide",
    ar: "يرجى تقديم عنوان بريد إلكتروني صحيح"
  },
  "Email already used for subscription": {
    en: "This email has already been used for a subscription. Please use a different email address.",
    fr: "Cet e-mail a déjà été utilisé pour un abonnement. Veuillez utiliser une adresse e-mail différente.",
    ar: "تم استخدام هذا البريد الإلكتروني بالفعل للاشتراك. يرجى استخدام عنوان بريد إلكتروني مختلف."
  },
  // Phone validation errors
  "Phone is required": {
    en: "Phone number is required",
    fr: "Le numéro de téléphone est obligatoire",
    ar: "رقم الهاتف مطلوب"
  },
  "Please provide a valid phone number": {
    en: "Please provide a valid phone number",
    fr: "Veuillez fournir un numéro de téléphone valide",
    ar: "يرجى تقديم رقم هاتف صحيح"
  },
  "Phone number too long": {
    en: "Phone number is too long",
    fr: "Le numéro de téléphone est trop long",
    ar: "رقم الهاتف طويل جدًا"
  },
  // Name validation errors
  "Invalid name format": {
    en: "Invalid name format. Please enter a valid name.",
    fr: "Format de nom invalide. Veuillez entrer un nom valide.",
    ar: "تنسيق الاسم غير صحيح. يرجى إدخال اسم صحيح."
  },
  "Name is required": {
    en: "Name is required",
    fr: "Le nom est obligatoire",
    ar: "الاسم مطلوب"
  },
  // Email service errors
  "Email service unavailable": {
    en: "Email service is currently unavailable. Please try again later.",
    fr: "Le service de courrier électronique n'est actuellement pas disponible. Veuillez réessayer plus tard.",
    ar: "خدمة البريد الإلكتروني غير متاحة حاليًا. يرجى محاولة مجددًا لاحقًا."
  },
  // Rate limit errors
  "You have reached the maximum number of emails (3). Please try again later.": {
    en: "You have reached the maximum number of subscription requests (3). Please try again later.",
    fr: "Vous avez atteint le nombre maximum de demandes d'abonnement (3). Veuillez réessayer plus tard.",
    ar: "لقد وصلت إلى الحد الأقصى من طلبات الاشتراك (3). يرجى محاولة مجددًا لاحقًا."
  },
  "Your IP has been temporarily blocked": {
    en: "Your IP has been temporarily blocked due to excessive requests. Please try again later.",
    fr: "Votre IP a été temporairement bloquée en raison de demandes excessives. Veuillez réessayer plus tard.",
    ar: "تم حظر عنوان IP الخاص بك مؤقتًا بسبب طلبات مفرطة. يرجى محاولة مجددًا لاحقًا."
  },
  "Too many requests. Please try again later.": {
    en: "Too many requests. Please try again later.",
    fr: "Trop de demandes. Veuillez réessayer plus tard.",
    ar: "عدد كبير جدًا من الطلبات. يرجى محاولة مجددًا لاحقًا."
  },
  // Generic errors
  "Failed to send subscription request": {
    en: "Failed to send your subscription request. Please try again later.",
    fr: "Impossible d'envoyer votre demande d'abonnement. Veuillez réessayer plus tard.",
    ar: "فشل إرسال طلب الاشتراك الخاص بك. يرجى محاولة مجددًا لاحقًا."
  }
};

// Helper function to get translated error message
const getErrorMessage = (backendError, language) => {
  if (!backendError) return "An error occurred. Please try again later.";
  
  // Check exact match first
  if (ERROR_MESSAGES[backendError]) {
    return ERROR_MESSAGES[backendError][language] || ERROR_MESSAGES[backendError]["en"];
  }
  
  // Check for partial matches
  for (const [key, translations] of Object.entries(ERROR_MESSAGES)) {
    if (backendError.includes(key) || key.includes(backendError)) {
      return translations[language] || translations["en"];
    }
  }
  
  // Default fallback
  const fallbackMessages = {
    en: "An error occurred while processing your request. Please try again later.",
    fr: "Une erreur s'est produite lors du traitement de votre demande. Veuillez réessayer plus tard.",
    ar: "حدث خطأ أثناء معالجة طلبك. يرجى محاولة مجددًا لاحقًا."
  };
  
  return fallbackMessages[language] || fallbackMessages["en"];
};


// Get English plan data using key or index
const getEnglishPlan = (planKey) => {
  if (typeof planKey === 'number') {
    // Fallback for numeric index
    const englishPlans = Object.values(PRICING.en.plans);
    return englishPlans[planKey] || null;
  }
  // Use string key directly
  return PRICING.en.plans[planKey] || null;
};

const PlanSelectionModal = ({ plan, isOpen, onClose, billingCycle, onPlanSelected }) => {
  const language = useSelector((state) => state.language);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    organization: "",
    country: "",
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Reset message when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setMessage("");
    }
  }, [isOpen]);

  // Cooldown timer effect
  React.useEffect(() => {
    if (cooldownRemaining <= 0) return;

    const timer = setInterval(() => {
      setCooldownRemaining((prev) => {
        if (prev <= 1000) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldownRemaining]);



  // Get localized plan data based on current language
  const getLocalizedPlan = (planKey) => {
    if (typeof planKey === 'number') {
      // Fallback for numeric index
      switch (language) {
        case "fr":
          return Object.values(PRICING.fr.plans)[planKey] || null;
        case "ar":
          return Object.values(PRICING.ar.plans)[planKey] || null;
        default:
          return Object.values(PRICING.en.plans)[planKey] || null;
      }
    }
    // Use string key directly
    switch (language) {
      case "fr":
        return PRICING.fr.plans[planKey] || null;
      case "ar":
        return PRICING.ar.plans[planKey] || null;
      default:
        return PRICING.en.plans[planKey] || null;
    }
  };



  if (!isOpen || !plan) return null;

  // Get plan key/index (support both planKey and planIndex for backwards compatibility)
  const planIdentifier = plan.planKey || plan.planIndex;
  
  // Get English plan data for backend (always English)
  const englishPlanData = getEnglishPlan(planIdentifier);
  // Get localized plan data for display (matches current language)
  const localizedPlanData = getLocalizedPlan(planIdentifier);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear validation message when user starts typing
    if (message.includes("required")) {
      setMessage("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check cooldown
    if (cooldownRemaining > 0) {
      const seconds = Math.ceil(cooldownRemaining / 1000);
      const cooldownMsg = language === "ar"
        ? `❌ يرجى الانتظار ${seconds} ثانية قبل إرسال طلب آخر`
        : language === "fr"
        ? `❌ Veuillez attendre ${seconds} secondes avant d'envoyer une autre demande`
        : `❌ Please wait ${seconds} seconds before sending another request`;
      setMessage(cooldownMsg);
      return;
    }

    // Validate form
    if (!formData.full_name || !formData.email || !formData.phone) {
      const validationMsg = language === "ar"
        ? "❌ يرجى ملء جميع الحقول المطلوبة"
        : language === "fr"
        ? "❌ Veuillez remplir tous les champs obligatoires"
        : "❌ Please fill in all required fields";
      setMessage(validationMsg);
      return;
    }

    setLoading(true);
    setMessage("");

    try {
      const planData = getEnglishPlan(planIdentifier);
      if (!planData) {
        setMessage("Error: Plan not found");
        setLoading(false);
        return;
      }

      // Prepare data for backend (always use English plan data)
      const subscriptionData = {
        name: formData.full_name,
        email: formData.email,
        billingCycle: billingCycle,
        planDetails: {
          name: planData.name,
          description: planData.description,
          price: billingCycle === "yearly" ? planData.price * 0.9 : planData.price,
          currency: planData.currency,
          billingPeriod: billingCycle,
          features: planData.features,
          phone: formData.phone,
          organization: formData.organization,
          country: formData.country,
        },
      };

      // Send to backend
      const response = await fetch("https://api.raqibapp.com/public/email/subscription", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(subscriptionData),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to send subscription request");
      }

      // Success message
      const successMsg = language === "ar" 
        ? "✓ تم إرسال طلبك بنجاح!\n\nسيتصل بك فريق الدعم قريباً. شكراً لاختيارك RAQIB"
        : language === "fr"
        ? "✓ Votre demande a été envoyée avec succès!\n\nNotre équipe d'assistance vous contactera bientôt. Merci de choisir RAQIB"
        : "✓ Your request has been sent successfully!\n\nOur support team will contact you soon. Thank you for choosing RAQIB";
      
      // Save selected plan to localStorage with localized names
      const allPlans = {
        en: Object.values(PRICING.en.plans),
        fr: Object.values(PRICING.fr.plans),
        ar: Object.values(PRICING.ar.plans),
      };
      
      const planInfo = {
        planKey: planIdentifier,
        billingCycle: billingCycle,
        nameEn: englishPlanData.name,
        nameFr: allPlans.fr[planIdentifier]?.name || englishPlanData.name,
        nameAr: allPlans.ar[planIdentifier]?.name || englishPlanData.name,
        descEn: englishPlanData.description,
        descFr: allPlans.fr[planIdentifier]?.description || englishPlanData.description,
        descAr: allPlans.ar[planIdentifier]?.description || englishPlanData.description,
        price: englishPlanData.price,
        currency: englishPlanData.currency,
      };
      
      localStorage.setItem("selectedPlan", JSON.stringify(planInfo));
      
      // Notify parent component
      if (onPlanSelected) {
        onPlanSelected(planInfo);
      }
      
      setMessage(successMsg);

      // Set cooldown after successful submission
      setCooldownRemaining(POST_COOLDOWN);

      setTimeout(() => {
        setFormData({
          full_name: "",
          email: "",
          phone: "",
          organization: "",
          country: "",
        });
        onClose();
      }, 3000);
    } catch (error) {
      console.error("Error sending subscription:", error);
      
      const langKey = language === "ar" ? "ar" : language === "fr" ? "fr" : "en";
      let errorMsg;
      
      // Get translated error message
      if (error.message) {
        errorMsg = getErrorMessage(error.message, langKey);
      } else {
        errorMsg = getErrorMessage("Failed to send subscription request", langKey);
      }
      
      setMessage("❌ " + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-800 to-teal-600 text-white p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold">
            {language === "ar" ? "اختر الخطة" : language === "fr" ? "Sélectionner le plan" : "Select Plan"}
          </h2>
          <button
            onClick={onClose}
            className="text-xl hover:text-gray-200 transition"
          >
            ✕
          </button>
        </div>

        <div className="p-6">
          {/* Plan Details Display */}
          {localizedPlanData && (
            <div className="mb-6 bg-gray-50 p-6 rounded-lg border-l-4 border-teal-500">
              <h3 className="text-2xl font-bold text-teal-600 mb-2">{localizedPlanData.name}</h3>
              <p className="text-gray-600 mb-4">{localizedPlanData.description}</p>
              
              {localizedPlanData.price !== null ? (
                <>
                  <div className="text-4xl font-bold text-teal-600">
                    {billingCycle === "yearly" ? (localizedPlanData.price * 0.9).toFixed(2) : localizedPlanData.price}
                    <span className="text-base text-gray-500 ml-2">{localizedPlanData.currency}</span>
                  </div>
                  <div className="text-sm text-gray-500 mt-2">
                    {billingCycle === "yearly"
                      ? (language === "ar" ? "سنويًا" : language === "fr" ? "Annuellement" : "Yearly")
                      : (language === "ar" ? "شهريًا" : language === "fr" ? "Mensuellement" : "Monthly")}
                  </div>
                  {billingCycle === "yearly" && localizedPlanData.price !== null && (
                    <div className="text-xs text-green-600 font-semibold mt-1">
                      {language === "ar" ? "تم توفير 10%" : language === "fr" ? "Économiser 10%" : "You save 10%"}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-3xl font-bold text-teal-600">Custom Pricing</div>
              )}

              {localizedPlanData.notes && (
                <p className="text-xs text-gray-600 mt-3 italic bg-white p-2 rounded">
                  {localizedPlanData.notes}
                </p>
              )}

              {/* Features */}
              <div className="mt-6">
                <h4 className="font-semibold text-gray-700 mb-3">
                  {language === "ar" ? "الميزات:" : language === "fr" ? "Fonctionnalités:" : "Features:"}
                </h4>
                <ul className="space-y-2">
                  {Object.values(localizedPlanData.features).map((feature, i) => {
                    const isEnabled = feature.value === true || (typeof feature.value === 'string' && feature.value !== false);
                    return (
                      <li
                        key={i}
                        className={`flex items-start text-sm ${
                          isEnabled ? "text-gray-700" : "text-gray-400"
                        }`}
                      >
                        {isEnabled ? (
                          <svg
                            className={`w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0 ${
                              language === "ar" ? "ml-2" : "mr-2"
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 13l4 4L19 7"
                            ></path>
                          </svg>
                        ) : (
                          <svg
                            className={`w-4 h-4 text-gray-300 mt-0.5 flex-shrink-0 ${
                              language === "ar" ? "ml-2" : "mr-2"
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M6 18L18 6M6 6l12 12"
                            ></path>
                          </svg>
                        )}
                        <div className="flex flex-col">
                          <span className={isEnabled ? "" : "line-through"}>
                            {feature.name}
                          </span>
                          {feature.description && (
                            <span className="text-xs opacity-70">
                              {feature.description}
                            </span>
                          )}
                          {typeof feature.value === 'string' && feature.value !== false && (
                            <span className="text-xs font-semibold text-teal-600 mt-1">
                              ({feature.value})
                            </span>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {language === "ar" ? "الاسم الكامل *" : language === "fr" ? "Nom complet *" : "Full Name *"}
              </label>
              <input
                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                placeholder={language === "ar" ? "أدخل اسمك الكامل" : language === "fr" ? "Votre nom complet" : "Your full name"}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {language === "ar" ? "البريد الإلكتروني *" : language === "fr" ? "Email *" : "Email *"}
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                placeholder={language === "ar" ? "your@email.com" : "your@email.com"}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {language === "ar" ? "الهاتف *" : language === "fr" ? "Téléphone *" : "Phone *"}
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                placeholder="+1 (555) 000-0000"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {language === "ar" ? "المنظمة" : language === "fr" ? "Organisation" : "Organization"}
              </label>
              <input
                type="text"
                name="organization"
                value={formData.organization}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                placeholder={language === "ar" ? "اسم عيادتك أو مستشفاك" : language === "fr" ? "Nom de votre clinique" : "Your clinic/hospital name"}
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {language === "ar" ? "الدولة" : language === "fr" ? "Pays" : "Country"}
              </label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-500"
                placeholder={language === "ar" ? "دولتك" : language === "fr" ? "Votre pays" : "Your country"}
              />
            </div>

            {message && (
              <div
                className={`p-4 rounded-lg text-sm whitespace-pre-line font-semibold ${
                  message.startsWith("✓")
                    ? "bg-green-100 text-green-800 border-2 border-green-400"
                    : "bg-red-100 text-red-800 border-2 border-red-400"
                }`}
              >
                {message}
              </div>
            )}

            {!message && (
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition"
                >
                  {language === "ar" ? "إلغاء" : language === "fr" ? "Annuler" : "Cancel"}
                </button>
                <button
                  type="submit"
                  disabled={loading || cooldownRemaining > 0}
                  className="flex-1 px-4 py-2 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-400 text-white font-semibold rounded-lg transition"
                >
                  {cooldownRemaining > 0
                    ? (language === "ar" 
                      ? `⏳ انتظر ${Math.ceil(cooldownRemaining / 1000)}ث`
                      : language === "fr"
                      ? `⏳ Attendez ${Math.ceil(cooldownRemaining / 1000)}s`
                      : `⏳ Wait ${Math.ceil(cooldownRemaining / 1000)}s`)
                    : loading 
                    ? (language === "ar" ? "جاري الإرسال..." : language === "fr" ? "Envoi..." : "Sending...")
                    : (language === "ar" ? "إرسال" : language === "fr" ? "Soumettre" : "Submit")
                  }
                </button>
              </div>
            )}

            {message && message.startsWith("❌") && (
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition"
                >
                  {language === "ar" ? "إغلاق" : language === "fr" ? "Fermer" : "Close"}
                </button>
                <button
                  type="submit"
                  disabled={loading || cooldownRemaining > 0}
                  className="flex-1 px-4 py-2 bg-teal-500 hover:bg-teal-600 disabled:bg-gray-400 text-white font-semibold rounded-lg transition"
                >
                  {cooldownRemaining > 0
                    ? (language === "ar" 
                      ? `⏳ انتظر ${Math.ceil(cooldownRemaining / 1000)}ث`
                      : language === "fr"
                      ? `⏳ Attendez ${Math.ceil(cooldownRemaining / 1000)}s`
                      : `⏳ Wait ${Math.ceil(cooldownRemaining / 1000)}s`)
                    : loading 
                    ? (language === "ar" ? "إعادة المحاولة..." : language === "fr" ? "Nouvelle tentative..." : "Retrying...")
                    : (language === "ar" ? "إعادة المحاولة" : language === "fr" ? "Réessayer" : "Try Again")
                  }
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default PlanSelectionModal;
